import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Phone,
  MessageCircle,
  Navigation,
  Clock,
  MapPin,
  Globe,
  CheckCircle2,
  Loader2,
  Sparkles,
  Share2,
  Copy,
  X,
} from "lucide-react";
import { getPlaceDetails, getPlaceSummary } from "../lib/api";
import { toast } from "sonner";
import ThemeToggle from "../components/ThemeToggle";

function getStoredLocation() {
  try {
    return JSON.parse(sessionStorage.getItem("gp_loc") || "null");
  } catch {
    return null;
  }
}

function phoneDigits(phone) {
  return (phone || "").replace(/\D/g, "");
}

export default function ProviderDetail() {
  const { placeId } = useParams();
  const [searchParams] = useSearchParams();
  const service = searchParams.get("service");
  const navigate = useNavigate();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);


  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getPlaceDetails(placeId);
        setPlace(data);
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "Falha ao carregar detalhes.";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
    // Fetch AI summary in parallel
    (async () => {
      setSummaryLoading(true);
      try {
        const s = await getPlaceSummary(placeId, service || undefined);
        setSummary(s.summary || "");
      } catch (e) {
        // Silently fail - summary is a nice-to-have
        setSummary("");
      } finally {
        setSummaryLoading(false);
      }
    })();
  }, [placeId, service]);

  const userLoc = getStoredLocation();

  // Pre-compute action URLs so we can render <a> tags (most reliable for tel: on mobile)
  const phoneRaw = phoneDigits(place?.national_phone || place?.international_phone);
  const phoneIntl = phoneRaw
    ? (phoneRaw.startsWith("55") ? phoneRaw : "55" + phoneRaw)
    : "";
  // Strip country code 55 from dialing format to leave only DDD + number
  const phoneNo55 = (phoneRaw.startsWith("55") && phoneRaw.length > 10) ? phoneRaw.slice(2) : phoneRaw;
  const telHref = phoneNo55 ? `tel:${phoneNo55}` : null;
  const waHref = phoneIntl
    ? `https://wa.me/${phoneIntl}?text=${encodeURIComponent("Olá! Preciso de assistência automotiva.")}`
    : null;
  const routeHref = place
    ? (userLoc
        ? `https://www.google.com/maps/dir/?api=1&origin=${userLoc.lat},${userLoc.lng}&destination=${place.lat},${place.lng}&travelmode=driving`
        : (place.google_maps_uri || `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}&destination_place_id=${place.place_id}`))
    : null;

  const handleCopyLink = () => {
    if (!userLoc) return;
    const link = `https://maps.google.com/?q=${userLoc.lat},${userLoc.lng}`;
    navigator.clipboard.writeText(link)
      .then(() => {
        toast.success("Link da localização copiado com sucesso!");
        setShowShareModal(false);
      })
      .catch((err) => {
        toast.error("Falha ao copiar o link.");
        console.error(err);
      });
  };

  const handleShareLocation = async () => {
    if (!userLoc) return;
    const link = `https://maps.google.com/?q=${userLoc.lat},${userLoc.lng}`;
    const shareData = {
      title: "Minha Localização - GoProtege",
      text: "Minha localização atual de socorro no Google Maps:",
      url: link,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        setShowShareModal(false);
        return;
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("Erro ao usar Web Share API:", err);
      }
    }

    // Fallback: Share via WhatsApp to the provider if possible, or open a general share link
    if (waHref) {
      const customText = `Olá! Preciso de assistência automotiva. Minha localização atual é: ${link}`;
      const customWaHref = waHref.split("?")[0] + `?text=${encodeURIComponent(customText)}`;
      window.open(customWaHref, "_blank");
    } else {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent("Minha localização atual: " + link)}`, "_blank");
    }
    setShowShareModal(false);
  };


  return (
    <div className="app-shell pb-32" data-testid="provider-detail-screen">
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center"
        >
          <ArrowLeft size={18} className="text-slate-700 dark:text-slate-200" />
        </button>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900 dark:text-white text-lg leading-none">Detalhes do prestador</div>
        </div>
        <ThemeToggle />
      </header>

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 text-slate-500">
          <Loader2 size={32} className="animate-spin text-[#00D285]" />
          <p className="mt-3 text-sm">Carregando...</p>
        </div>
      )}

      {!loading && error && (
        <div className="m-5 bg-red-50 border border-red-100 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {!loading && place && (
        <main className="px-5 pt-4 pb-8 space-y-5 fade-in">
          {/* Hero card */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-5 shadow-md">
            <h2 className="font-display text-2xl font-bold leading-tight" data-testid="provider-name">
              {place.name}
            </h2>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#00D285]/20 text-[#00D285] font-semibold">
                <CheckCircle2 size={12} /> Parceiro verificado
              </span>
              {place.rating != null && (
                <span className="inline-flex items-center gap-1 text-amber-400 font-semibold">
                  <Star size={12} fill="#fbbf24" strokeWidth={0} /> {place.rating.toFixed(1)}
                  {place.user_ratings_total != null && (
                    <span className="text-slate-300 font-normal">
                      ({place.user_ratings_total} avaliações)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Info rows */}
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl divide-y divide-slate-100 dark:divide-slate-800">
            <InfoRow icon={MapPin} label="Endereço" value={place.address || "—"} />
            {place.national_phone && (
              <InfoRow
                icon={Phone}
                label="Telefone"
                value={place.national_phone}
                href={telHref || undefined}
                testid="provider-phone"
              />
            )}
            <InfoRow
              icon={Clock}
              label="Horário"
              value={
                place.open_now === true
                  ? "Aberto agora"
                  : place.open_now === false
                  ? "Fechado"
                  : "Horário não informado"
              }
            />
            {place.website && (
              <InfoRow
                icon={Globe}
                label="Website"
                value={place.website}
                href={place.website}
              />
            )}
          </div>

          {/* AI summary - placed AFTER all info rows */}
          <div
            className="bg-white dark:bg-slate-900 border border-[#00D285]/30 rounded-2xl p-4 shadow-sm"
            data-testid="provider-summary"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-lg bg-[#00D285]/15 flex items-center justify-center">
                <Sparkles size={14} className="text-[#00D285]" />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#00B874]">
                Visão geral
              </div>
            </div>
            {summaryLoading ? (
              <p className="text-sm text-slate-400 dark:text-slate-500 italic">Carregando mais detalhes...</p>
            ) : summary ? (
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{summary}</p>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                Resumo indisponível no momento.
              </p>
            )}
          </div>

          {/* Compartilhamento de localização */}
          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800/80 rounded-2xl p-4 flex items-center justify-between gap-4 stagger">
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800 dark:text-white">Precisa informar sua localização?</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {userLoc 
                  ? "Envie sua localização exata para reduzir o tempo de espera e agilizar o resgate."
                  : "Ative a localização nas configurações do app/dispositivo para compartilhar seu ponto de parada."}
              </div>
            </div>
            {userLoc ? (
              <button
                onClick={() => setShowShareModal(true)}
                className="btn-press flex items-center gap-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0"
              >
                <Share2 size={14} className="text-[#00D285]" /> Compartilhar
              </button>
            ) : (
              <button
                onClick={() => toast.info("Ative a localização na tela inicial para compartilhar.")}
                className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 shrink-0 cursor-not-allowed"
                disabled
              >
                <Share2 size={14} /> Compartilhar
              </button>
            )}
          </div>

          <div className="text-xs text-slate-400 dark:text-slate-500 px-1">
            Dados fornecidos pelo Google Places. Verifique informações antes de contratar.
          </div>
        </main>
      )}

      {/* Sticky CTAs */}
      {!loading && place && (
        <div
          className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-100 dark:border-slate-800 px-5 py-3 z-40 shadow-[0_-8px_24px_rgba(15,23,42,0.06)]"
          data-testid="provider-actions"
        >
          <div className="flex gap-2 mb-2">
            {telHref ? (
              <a
                data-testid="action-call"
                href={telHref}
                target="_top"
                className="btn-press flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 no-underline"
              >
                <Phone size={16} /> Ligar
              </a>
            ) : (
              <button
                data-testid="action-call"
                onClick={() => toast.info("Este prestador não disponibilizou telefone no Google.")}
                className="btn-press flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <Phone size={16} /> Ligar
              </button>
            )}
            {waHref ? (
              <a
                data-testid="action-whatsapp"
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="btn-press flex-1 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 no-underline"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            ) : (
              <button
                data-testid="action-whatsapp"
                onClick={() => toast.info("Este prestador não disponibilizou WhatsApp.")}
                className="btn-press flex-1 bg-slate-200 text-slate-400 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <MessageCircle size={16} /> WhatsApp
              </button>
            )}
          </div>
          <a
            data-testid="action-route"
            href={routeHref}
            target="_blank"
            rel="noreferrer"
            className="btn-press w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 no-underline"
          >
            <Navigation size={16} /> Traçar rota
          </a>
        </div>
      )}

      {/* Bottom Sheet de Compartilhamento */}
      {showShareModal && userLoc && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowShareModal(false)}
          />
          
          {/* Sheet */}
          <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-t-3xl p-6 shadow-2xl z-50 animate-slide-up border-t border-slate-100 dark:border-slate-800">
            {/* Handle bar */}
            <div className="mx-auto w-12 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 mb-5" />
            
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Compartilhar Minha Localização
              </h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <X size={16} />
              </button>
            </div>
            
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
              O link abaixo contém suas coordenadas geográficas atuais. O prestador poderá abrir este link diretamente no GPS para navegar até você.
            </p>
            
            {/* Preview Box */}
            <div className="bg-slate-50 dark:bg-slate-850 border border-slate-100 dark:border-slate-800 rounded-xl p-3.5 mb-6 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#00D285]/10 flex items-center justify-center shrink-0">
                <MapPin size={20} className="text-[#00D285]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-800 dark:text-white truncate">Link do Google Maps</div>
                <div className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
                  https://maps.google.com/?q={userLoc.lat},{userLoc.lng}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={handleShareLocation}
                className="w-full bg-[#00D285] hover:bg-[#00B874] text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors duration-200 shadow-md"
              >
                <Share2 size={16} /> Enviar Localização
              </button>
              
              <button
                onClick={handleCopyLink}
                className="w-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-200"
              >
                <Copy size={16} /> Copiar Link do Mapa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, multiline, href, testid }) {
  const isTel = typeof href === "string" && href.startsWith("tel:");
  return (
    <div className="flex items-start gap-3 p-4" data-testid={testid}>
      <div className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-slate-600 dark:text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
          {label}
        </div>
        {href ? (
          <a
            href={href}
            target={isTel ? "_top" : "_blank"}
            rel="noreferrer"
            className={`text-sm text-[#00D285] underline ${multiline ? "" : "truncate block"}`}
          >
            {value}
          </a>
        ) : (
          <div className={`text-sm text-slate-700 dark:text-slate-200 ${multiline ? "" : "truncate"}`}>{value}</div>
        )}
      </div>
    </div>
  );
}
